// Tasks page component
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { usePermissions } from '@/lib/hooks'
import { Button } from '@/components/ui/button'
import { Plus, Edit2, Trash2, ChevronRight } from 'lucide-react'
import TaskForm from '@/components/custom-ui/task-form'
import TaskDetail from '@/components/custom-ui/task-detail'
import { Database } from '@/../database.types'

type Task = Database['public']['Tables']['tasks']['Row']

export default function TasksPage() {
  const [showAddTask, setShowAddTask] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const queryClient = useQueryClient()
  const { currentProject } = useProject()
  const getToken = useGetToken()
  const permissions = usePermissions()

  const { data: tasks, isLoading } = useQuery<Task[]>({
    queryKey: ['tasks', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('tasks')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  const getSizeColor = (size: string | null) => {
    if (!size) return 'bg-gray-100 text-gray-800'
    switch (size) {
      case 'S': return 'bg-green-100 text-green-800'
      case 'M': return 'bg-yellow-100 text-yellow-800'
      case 'L': return 'bg-orange-100 text-orange-800'
      case 'XL': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return

    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)
    
    const { error } = await authenticatedClient
      .from('tasks')
      .delete()
      .eq('id', taskId)

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Tasks</h1>
        {permissions.canEditTasks && (
          <Button onClick={() => setShowAddTask(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {tasks && tasks.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              No tasks found. Add your first task to get started.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {tasks?.map((task) => (
                <div 
                  key={task.id} 
                  className="p-6 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedTask(task)}
                >
                  {/* Main row - Title and Actions */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {task.title}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {permissions.canEditTasks && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingTask(task)
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteTask(task.id)
                            }}
                            className="text-red-600 hover:text-red-900 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>

                  {/* Description row (if exists) */}
                  {task.description && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {task.description}
                      </p>
                    </div>
                  )}

                  {/* Founder leverage (if exists) */}
                  {task.founder_leverage && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-500 italic">
                        {task.founder_leverage}
                      </p>
                    </div>
                  )}

                  {/* Details row - Category, Sizes, and Frequency */}
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Category:</span>
                      <span className="font-medium text-gray-900">
                        {task.category || 'Uncategorized'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Effort:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSizeColor(task.tshirt_size_effort)}`}>
                        {task.tshirt_size_effort || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Capital:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSizeColor(task.tshirt_size_capital)}`}>
                        {task.tshirt_size_capital || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Frequency:</span>
                      <span className="font-medium text-gray-900">
                        {task.frequency || 'N/A'}
                      </span>
                    </div>

                    {/* Status if available */}
                    {task.status && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Status:</span>
                        <span className="font-medium text-gray-900 capitalize">
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Additional details row if available */}
                  {(task.start_date || task.due_date || task.estimated_effort_hours) && (
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      {task.start_date && (
                        <div>
                          Start: {new Date(task.start_date).toLocaleDateString()}
                        </div>
                      )}
                      {task.due_date && (
                        <div>
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </div>
                      )}
                      {task.estimated_effort_hours && (
                        <div>
                          Estimated: {task.estimated_effort_hours}h
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Task Form */}
      <TaskForm
        open={showAddTask || !!editingTask}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddTask(false)
            setEditingTask(null)
          }
        }}
        task={editingTask ? {
          id: editingTask.id,
          title: editingTask.title,
          category: editingTask.category || '',
          tshirt_size_effort: editingTask.tshirt_size_effort || 'M',
          tshirt_size_capital: editingTask.tshirt_size_capital || 'M',
          frequency: editingTask.frequency || '',
          founder_leverage: editingTask.founder_leverage || ''
        } : undefined}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['tasks'] })
          setShowAddTask(false)
          setEditingTask(null)
        }}
      />

      {/* Task Detail Dialog */}
      <TaskDetail
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTask(null)
          }
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['tasks'] })
        }}
      />
    </div>
  )
}
